import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Drawer from "@intility/bifrost-react/Drawer";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Table from "@intility/bifrost-react/Table";
import type { Columns } from "@intility/gjallarbru";
import { DataTable, DateCell, dateColumnDefaults } from "@intility/gjallarbru";
import type { MRT_Cell } from "material-react-table";
import { useCallback, useEffect, useState } from "react";
import type { ActivityEvent, ActivityTuple } from "~/api/activity";
import { getRuntimeActivity } from "~/api/activity";
import { FgaErrorCodes, IntilityIdException } from "~/api/errorEnvelope";
import { PageLayout } from "~/components/layout/PageLayout";
import { relationBadgeState } from "~/lib/relationBadges";

const OPERATIONS = [
  { label: "Role grant", value: "role.grant" },
  { label: "Role revoke", value: "role.revoke" },
  { label: "Role promote", value: "role.promote" },
  { label: "Role demote", value: "role.demote" },
  { label: "Resource create", value: "resource.create" },
  { label: "Tuple write", value: "tuple.write" },
  { label: "Tuple delete", value: "tuple.delete" },
  { label: "Link create", value: "link.create" },
  { label: "Group member add", value: "group.member.add" },
  { label: "Group member remove", value: "group.member.remove" },
  { label: "Company create", value: "company.create" },
  { label: "Company delete", value: "company.delete" },
  { label: "Platform create", value: "platform.create" },
  { label: "Platform delete", value: "platform.delete" },
  { label: "Resource type create", value: "resource-type.create" },
  { label: "Resource type update", value: "resource-type.update" },
  { label: "Resource type delete", value: "resource-type.delete" },
  { label: "Blueprint placement create", value: "blueprint-placement.create" },
  { label: "Blueprint placement update", value: "blueprint-placement.update" },
  { label: "Blueprint placement delete", value: "blueprint-placement.delete" },
];

const OUTCOMES = [
  { label: "Succeeded", value: "succeeded" },
  { label: "Unchanged", value: "unchanged" },
  { label: "Rejected", value: "rejected" },
  { label: "Unauthorized", value: "unauthorized" },
  { label: "Failed", value: "failed" },
  { label: "Pending", value: "pending" },
];

function operationBadgeState(op: string): "success" | "alert" | "chill" {
  if (
    op.endsWith(".create") ||
    op.endsWith(".grant") ||
    op.endsWith(".add") ||
    op.endsWith(".write")
  )
    return "success";
  if (
    op.endsWith(".delete") ||
    op.endsWith(".revoke") ||
    op.endsWith(".remove")
  )
    return "alert";
  return "chill";
}

function OutcomeCell({ outcome }: { outcome: string }) {
  if (outcome === "succeeded") return <span>{outcome}</span>;
  // #498: unchanged is neither a success nor a failure — it says the attempted
  // write found the end state already in place.
  if (outcome === "unchanged")
    return (
      <span style={{ color: "var(--bfc-status-warning)" }}>{outcome}</span>
    );
  if (
    outcome === "failed" ||
    outcome === "rejected" ||
    outcome === "unauthorized"
  )
    return <span style={{ color: "var(--bfc-status-danger)" }}>{outcome}</span>;
  return <span className="bfc-base-2">{outcome}</span>;
}

function formatOperation(op: string): string {
  return OPERATIONS.find((o) => o.value === op)?.label ?? op;
}

function actorDisplayName(event: ActivityEvent): string {
  if (event.actorInfo?.displayName) return event.actorInfo.displayName;
  if (event.actorInfo?.username) return event.actorInfo.username;
  return event.actor;
}

function TupleTable({ tuples }: { tuples: ActivityTuple[] }) {
  if (tuples.length === 0) return <p className="bf-p bfc-base-2">None</p>;
  return (
    <Table noBorder>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>User</Table.HeaderCell>
          <Table.HeaderCell>Relation</Table.HeaderCell>
          <Table.HeaderCell>Object</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {tuples.map((t) => (
          <Table.Row key={`${t.user}|${t.relation}|${t.object}`}>
            <Table.Cell>
              <Badge state="chill" pill>
                {t.user}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Badge state={relationBadgeState(t.relation, "access")}>
                {t.relation}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Badge state="neutral" pill>
                {t.object}
              </Badge>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

const columns: Columns<ActivityEvent> = [
  {
    ...dateColumnDefaults,
    accessorKey: "occurredAt",
    Cell: ({ cell }: { cell: MRT_Cell<ActivityEvent> }) => (
      <DateCell cell={cell} displayMode="datetime" />
    ),
    header: "Time",
    size: 180,
  },
  {
    accessorKey: "operation",
    Cell: ({ cell }: { cell: MRT_Cell<ActivityEvent> }) => {
      const op = cell.getValue<string>();
      return (
        <Badge state={operationBadgeState(op)} pill>
          {formatOperation(op)}
        </Badge>
      );
    },
    filterSelectOptions: OPERATIONS,
    filterVariant: "select",
    header: "Operation",
    size: 180,
  },
  {
    accessorKey: "actor",
    Cell: ({ row }: { row: { original: ActivityEvent } }) => (
      <span title={row.original.actor}>{actorDisplayName(row.original)}</span>
    ),
    header: "Actor",
    size: 160,
  },
  {
    accessorKey: "actorClient",
    header: "Client",
    size: 160,
  },
  {
    id: "subject",
    accessorFn: (row) => row.tuples.map((t) => t.user).join(", "),
    Cell: ({ row }: { row: { original: ActivityEvent } }) => {
      const tuples = row.original.tuples;
      if (tuples.length === 0) return null;
      const users = [...new Set(tuples.map((t) => t.user))];
      if (users.length === 1) return <span>{users[0]}</span>;
      return (
        <span>
          {users[0]}{" "}
          <Badge state="neutral" pill>
            +{users.length - 1}
          </Badge>
        </span>
      );
    },
    enableColumnFilter: false,
    header: "Subject",
    size: 180,
  },
  {
    id: "relation",
    accessorFn: (row) => row.tuples.map((t) => t.relation).join(", "),
    Cell: ({ row }: { row: { original: ActivityEvent } }) => {
      const tuples = row.original.tuples;
      if (tuples.length === 0) return null;
      const relations = [...new Set(tuples.map((t) => t.relation))];
      if (relations.length === 1)
        return (
          <Badge state={relationBadgeState(relations[0], "access")} pill>
            {relations[0]}
          </Badge>
        );
      return (
        <span>
          <Badge state={relationBadgeState(relations[0], "access")} pill>
            {relations[0]}
          </Badge>{" "}
          <Badge state="neutral" pill>
            +{relations.length - 1}
          </Badge>
        </span>
      );
    },
    enableColumnFilter: false,
    header: "Relation",
    size: 140,
  },
  {
    id: "resource",
    accessorFn: (row) => row.tuples.map((t) => t.object).join(", "),
    Cell: ({ row }: { row: { original: ActivityEvent } }) => {
      const tuples = row.original.tuples;
      if (tuples.length === 0) return null;
      const objects = [...new Set(tuples.map((t) => t.object))];
      if (objects.length === 1) return <span>{objects[0]}</span>;
      return (
        <span>
          {objects[0]}{" "}
          <Badge state="neutral" pill>
            +{objects.length - 1}
          </Badge>
        </span>
      );
    },
    enableColumnFilter: false,
    header: "Resource",
    size: 220,
  },
  {
    accessorKey: "outcome",
    Cell: ({ cell }: { cell: MRT_Cell<ActivityEvent> }) => (
      <OutcomeCell outcome={cell.getValue<string>()} />
    ),
    filterSelectOptions: OUTCOMES,
    filterVariant: "select",
    header: "Outcome",
    size: 120,
  },
];

export default function ActivityLog() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(
    null,
  );
  const [root, setRoot] = useState("");
  // The root the current list was loaded with. "Load more" must page within
  // it, even when the operator has typed a new root without pressing Apply.
  const [appliedRoot, setAppliedRoot] = useState("");

  const loadEvents = useCallback(async (rootFilter: string) => {
    setLoading(true);
    setError(null);
    setNextCursor(null);
    setAppliedRoot(rootFilter);
    try {
      const page = await getRuntimeActivity({
        limit: 50,
        root: rootFilter || undefined,
      });
      setEvents(page.events);
      setNextCursor(page.nextCursor);
      setInitialLoadDone(true);
    } catch (err) {
      if (
        err instanceof IntilityIdException &&
        err.code === FgaErrorCodes.UpstreamUnavailable
      ) {
        setError(
          "Audit log is not available for the access store. The audit database may not be configured.",
        );
        setEvents([]);
      } else {
        setError(err instanceof Error ? err.message : String(err));
        // A failed reload must not leave the previous root's table on
        // screen under the error banner.
        setEvents([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents("");
  }, [loadEvents]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await getRuntimeActivity({
        cursor: nextCursor,
        limit: 50,
        root: appliedRoot || undefined,
      });
      setEvents((prev) => [...prev, ...page.events]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Authorization" },
        { label: "Audit Log" },
      ]}
      title="Audit Log"
      description="Audit trail of authorization changes on the access store. Shows grant, revoke, and resource mutations scoped to objects you administer."
      fullWidthBody
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <Input
            label="Root"
            placeholder="platform:apm"
            value={root}
            onChange={(e) => setRoot(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadEvents(root);
            }}
          />
          <Button variant="basic" onClick={() => loadEvents(root)}>
            Apply
          </Button>
        </div>

        {error && (
          <Message state="alert" header="Could not load activity">
            {error}
          </Message>
        )}

        {loading && !initialLoadDone ? (
          <p className="bf-p">Loading activity...</p>
        ) : !error && events.length === 0 && initialLoadDone ? (
          <p className="bf-p bfc-base-2">No activity events found.</p>
        ) : events.length > 0 ? (
          <>
            <DataTable
              columns={columns}
              data={events}
              name="activity-log"
              bifrostTableRowProps={{
                onClick: (
                  _e: React.MouseEvent | React.KeyboardEvent,
                  row: { original: ActivityEvent },
                ) => setSelectedEvent(row.original),
              }}
            />

            {nextCursor && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="flat"
                  onClick={loadMore}
                  state={loadingMore ? "inactive" : undefined}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>

      <Drawer
        header="Event detail"
        isOpen={!!selectedEvent}
        onRequestClose={() => setSelectedEvent(null)}
      >
        {selectedEvent && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Table noBorder>
              <Table.Body>
                <Table.Row>
                  <Table.Cell className="bfc-base-2">Time</Table.Cell>
                  <Table.Cell>
                    {new Date(selectedEvent.occurredAt).toLocaleString()}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell className="bfc-base-2">Operation</Table.Cell>
                  <Table.Cell>
                    <Badge
                      state={operationBadgeState(selectedEvent.operation)}
                      pill
                    >
                      {formatOperation(selectedEvent.operation)}
                    </Badge>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell className="bfc-base-2">Outcome</Table.Cell>
                  <Table.Cell>
                    <OutcomeCell outcome={selectedEvent.outcome} />
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell className="bfc-base-2">Actor</Table.Cell>
                  <Table.Cell>
                    <span title={selectedEvent.actor}>
                      {actorDisplayName(selectedEvent)}
                    </span>
                  </Table.Cell>
                </Table.Row>
                {selectedEvent.actorClient && (
                  <Table.Row>
                    <Table.Cell className="bfc-base-2">Client</Table.Cell>
                    <Table.Cell>{selectedEvent.actorClient}</Table.Cell>
                  </Table.Row>
                )}
                {selectedEvent.errorCode && (
                  <Table.Row>
                    <Table.Cell className="bfc-base-2">Error</Table.Cell>
                    <Table.Cell>{selectedEvent.errorCode}</Table.Cell>
                  </Table.Row>
                )}
                {selectedEvent.traceId && (
                  <Table.Row>
                    <Table.Cell className="bfc-base-2">Trace ID</Table.Cell>
                    <Table.Cell>
                      <code style={{ fontSize: 12 }}>
                        {selectedEvent.traceId}
                      </code>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {selectedEvent.tuples.some((t) => t.action === "write") && (
              <div>
                <p className="bf-h4" style={{ marginBottom: 8 }}>
                  Tuples written{" "}
                  <Badge state="neutral" pill>
                    {
                      selectedEvent.tuples.filter((t) => t.action === "write")
                        .length
                    }
                  </Badge>
                </p>
                <TupleTable
                  tuples={selectedEvent.tuples.filter(
                    (t) => t.action === "write",
                  )}
                />
              </div>
            )}

            {selectedEvent.tuples.some((t) => t.action === "delete") && (
              <div>
                <p className="bf-h4" style={{ marginBottom: 8 }}>
                  Tuples deleted{" "}
                  <Badge state="neutral" pill>
                    {
                      selectedEvent.tuples.filter((t) => t.action === "delete")
                        .length
                    }
                  </Badge>
                </p>
                <TupleTable
                  tuples={selectedEvent.tuples.filter(
                    (t) => t.action === "delete",
                  )}
                />
              </div>
            )}

            {selectedEvent.tuples.some((t) => t.action === "absorb") && (
              <div>
                <p className="bf-h4" style={{ marginBottom: 8 }}>
                  Assignments replaced{" "}
                  <Badge state="neutral" pill>
                    {
                      selectedEvent.tuples.filter((t) => t.action === "absorb")
                        .length
                    }
                  </Badge>
                </p>
                <TupleTable
                  tuples={selectedEvent.tuples.filter(
                    (t) => t.action === "absorb",
                  )}
                />
              </div>
            )}

            {/* #637: an unchanged event carries the tuples it asked for, not tuples it
                wrote. Without its own section the drawer showed no tuples at all, so the
                event could not name its subject. */}
            {selectedEvent.tuples.some((t) => t.action === "unchanged") && (
              <div>
                <p className="bf-h4" style={{ marginBottom: 8 }}>
                  Already in effect{" "}
                  <Badge state="neutral" pill>
                    {
                      selectedEvent.tuples.filter(
                        (t) => t.action === "unchanged",
                      ).length
                    }
                  </Badge>
                </p>
                <TupleTable
                  tuples={selectedEvent.tuples.filter(
                    (t) => t.action === "unchanged",
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </PageLayout>
  );
}
